/** AssistantMessage — icon + name header, plain text, references, copy on hover. */

import React, { useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { ChevronDown, ChevronUp, Copy, Check, FileText } from "lucide-react";
import DOMPurify from "dompurify";
import {
  convertUrlsToMarkdown,
  extractAndReplaceImages,
  addImageLinksToText,
  getImageDisplayName,
  getUsedBlocks,
} from "../utils/messageUtils";
import { processReferenceWithUrls } from "./Utils";
import { Mermaid } from "./Mermaid";
import cogsolAvatar from "../assets/cogsol-logo.svg";
import { copyToClipboard } from "../utils/clipboard";

const DEFAULT_AVATAR = cogsolAvatar;

const getHostname = (url) => {
  try { return new URL(url).hostname; } catch { return url; }
};

const ImageGallery = ({ images }) => {
  if (images.length === 0) return null;
  return (
    <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 gap-2">
      {images.map((img, idx) => (
        <div key={idx} className="flex flex-col items-center">
          <button
            type="button"
            className="w-full aspect-video bg-gray-100 rounded-lg overflow-hidden cursor-pointer border border-gray-200 hover:opacity-90 transition-opacity p-0"
            onClick={() => window.open(img.src, "_blank", "noopener,noreferrer")}
            title={getImageDisplayName(img)}
          >
            <img
              src={img.src}
              alt={img.alt || img.label}
              className="w-full h-full object-cover"
              onError={(e) => { e.target.style.display = "none"; }}
            />
          </button>
          <span className="text-xs text-gray-500 mt-1 font-medium text-center">
            {getImageDisplayName(img)}
          </span>
        </div>
      ))}
    </div>
  );
};

const WebLinksPreview = ({ links }) => {
  if (links.length === 0) return null;
  return (
    <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 gap-2">
      {links.map((link, idx) => (
        <div key={idx} className="flex flex-col items-center">
          <button
            type="button"
            className="w-full aspect-video bg-gray-100 rounded-lg overflow-hidden cursor-pointer border border-gray-200 hover:border-blue-400 hover:shadow-md transition-all relative p-0"
            onClick={() => window.open(link.src, "_blank", "noopener,noreferrer")}
            title={link.src}
          >
            <img
              src={link.previewUrl}
              alt="Site preview"
              className="w-full h-full object-cover object-top"
              onError={(e) => {
                e.target.style.display = "none";
                if (e.target.nextSibling) e.target.nextSibling.style.display = "flex";
              }}
            />
            <div
              className="absolute inset-0 items-center justify-center bg-gradient-to-br from-blue-50 to-blue-100"
              style={{ display: "none" }}
            >
              <div className="text-center p-2">
                <svg className="w-8 h-8 mx-auto text-blue-400 mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
                </svg>
              </div>
            </div>
          </button>
          <button
            type="button"
            className="text-xs text-blue-600 mt-1 font-medium text-center truncate w-full hover:underline cursor-pointer bg-transparent border-none p-0"
            onClick={() => window.open(link.src, "_blank", "noopener,noreferrer")}
          >
            {getHostname(link.src)}
          </button>
        </div>
      ))}
    </div>
  );
};

const AssistantMessage = ({
  message, chatName, avatarUrl, nameColor, secondaryColor,
  expandedReferences, onToggleReferences, messageIndex,
}) => {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);

  const { processedMarkdown, closedMermaid, extractedImages, extractedWebLinks, usedBlocks } =
    useMemo(() => {
      const { text: processedText, images, webLinks } = extractAndReplaceImages(message.content);
      const textWithImageLinks = addImageLinksToText(processedText, [...images, ...webLinks]);
      const blocks = getUsedBlocks(message);
      const markdown = convertUrlsToMarkdown(
        textWithImageLinks.replace(/\\+/g, "\\")
      );
      // Charts from CLOSED ```mermaid fences only, so streaming shows plain
      // code until the block is complete.
      const closedMermaid = new Set(
        [...markdown.matchAll(/```mermaid[ \t]*\r?\n([\s\S]*?)\r?\n[ \t]*```/g)]
          .map((m) => m[1].trim())
      );
      return {
        processedMarkdown: markdown,
        closedMermaid,
        extractedImages: images,
        extractedWebLinks: webLinks,
        usedBlocks: blocks,
      };
    }, [message]);

  const isExpanded = expandedReferences?.[messageIndex];

  const handleCopy = async () => {
    const ok = await copyToClipboard(message.content);
    if (ok) {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    }
  };

  // Memoized so react-markdown keeps stable identities and doesn't remount the
  // subtree (incl. <Mermaid>) each render; closedMermaid is read via a ref.
  const closedMermaidRef = useRef(closedMermaid);
  closedMermaidRef.current = closedMermaid;

  const remarkPlugins = useMemo(() => [remarkGfm], []);

  const markdownComponents = useMemo(() => ({
    p: ({ children }) => <p className="my-2">{children}</p>,
    h1: ({ children }) => <h1 className="text-2xl font-bold mt-5 mb-2">{children}</h1>,
    h2: ({ children }) => <h2 className="text-xl font-bold mt-5 mb-2">{children}</h2>,
    h3: ({ children }) => <h3 className="text-lg font-semibold mt-4 mb-2">{children}</h3>,
    ol: ({ children }) => <ol className="list-decimal pl-6 my-2 space-y-1.5">{children}</ol>,
    ul: ({ children }) => <ul className="list-disc pl-6 my-2 space-y-1.5">{children}</ul>,
    li: ({ children }) => <li className="my-1">{children}</li>,
    a: ({ children, ...aProps }) => (
      <a target="_blank" rel="noopener noreferrer" className="text-blue-800 underline" {...aProps}>
        {children}
      </a>
    ),
    blockquote: ({ children }) => (
      <blockquote className="border-l-4 border-gray-300 pl-4 my-2 italic text-gray-600">{children}</blockquote>
    ),
    code: ({ children, ...props }) => (
      <code className="bg-gray-100 border border-gray-200 px-2 py-1 rounded-md text-sm font-mono" {...props}>{children}</code>
    ),
    pre: ({ children }) => {
      // Closed ```mermaid blocks render as diagrams; others stay plain <pre>.
      const child = Array.isArray(children) ? children[0] : children;
      const className = child?.props?.className || "";
      if (className.includes("language-mermaid")) {
        const chart = String(child.props.children).replace(/\n$/, "");
        if (closedMermaidRef.current.has(chart.trim())) {
          return <Mermaid chart={chart} />;
        }
      }
      return (
        <pre className="bg-gray-100 border border-gray-200 p-3 rounded-md my-2 text-sm overflow-x-auto [&>code]:p-0 [&>code]:bg-transparent [&>code]:border-none">{children}</pre>
      );
    },
    table: ({ children }) => (
      <div className="my-3 overflow-x-auto rounded-lg border border-gray-200">
        <table className="w-full border-collapse text-[14px] leading-relaxed">{children}</table>
      </div>
    ),
    thead: ({ children }) => <thead className="bg-gray-50">{children}</thead>,
    tbody: ({ children }) => (
      <tbody className="[&_tr:nth-child(even)]:bg-gray-50/60">{children}</tbody>
    ),
    tr: ({ children }) => <tr className="border-b border-gray-100 last:border-0">{children}</tr>,
    th: ({ children, style }) => (
      <th
        className="px-3 py-2 text-left font-semibold text-gray-700 border-b border-gray-300 whitespace-nowrap"
        style={style}
      >
        {children}
      </th>
    ),
    td: ({ children, style }) => (
      <td className="px-3 py-2 align-top text-gray-700" style={style}>{children}</td>
    ),
  }), []);

  return (
    <div className="group/msg flex flex-col gap-0.5 my-6">
      {/* Header: icon + name */}
      <div className="flex items-center gap-1.5 mb-1">
        <img
          src={avatarUrl || DEFAULT_AVATAR}
          alt={chatName || t("assistant")}
          className="w-8 h-8 rounded-md object-cover shrink-0"
          onError={(e) => { e.target.src = DEFAULT_AVATAR; }}
        />
        <span
          className="text-xs font-semibold text-gray-500 truncate max-w-[200px]"
          style={{ color: nameColor || undefined }}
        >
          {chatName || t("assistant")}
        </span>
      </div>

      {/* Message content — no bubble */}
      <div>
        <Markdown
          className="font-sans text-[15px] leading-relaxed"
          remarkPlugins={remarkPlugins}
          components={markdownComponents}
        >
          {processedMarkdown}
        </Markdown>
      </div>

      {/* References — collapsible with count badge */}
      {usedBlocks.length > 0 && (
        <div className="mt-2">
          <button
            type="button"
            className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium border rounded-lg bg-white hover:bg-gray-100 transition-colors cursor-pointer"
            style={{
              color: secondaryColor || "#6b7280",
              borderColor: secondaryColor || "#e5e7eb",
            }}
            onClick={() => onToggleReferences?.(messageIndex)}
          >
            <FileText size={13} />
            {usedBlocks.length} {usedBlocks.length === 1 ? t("referenceCount_one") : t("referenceCount_other")}
            {isExpanded ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
          </button>
          {isExpanded && message.references && (
            <div className="mt-1.5 px-3.5 py-2.5 bg-white rounded-lg text-xs text-gray-500 leading-relaxed shadow-sm">
              {message.references.map((block, idx) => (
                <p key={idx} className="mb-1 last:mb-0">
                  {"[" + block.reference_num + "] "}
                  <span dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(processReferenceWithUrls(block.source)) }} />
                </p>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Actions row — copy button appears on hover */}
      <div className="flex gap-0.5 mt-1 opacity-0 group-hover/msg:opacity-100 transition-opacity">
        <button
          type="button"
          className="w-[26px] h-[26px] rounded-md border-none bg-transparent cursor-pointer flex items-center justify-center text-gray-400 hover:bg-gray-100 hover:text-gray-900 transition-colors"
          onClick={handleCopy}
          title={copied ? t("copied") : t("copyMessage")}
        >
          {copied ? <Check size={14} /> : <Copy size={14} />}
        </button>
      </div>

      <ImageGallery images={extractedImages} />
      <WebLinksPreview links={extractedWebLinks} />
    </div>
  );
};

export { AssistantMessage };
